/**
 * WeeklySuccessCard.jsx — M8 (Phase 3 Engagement)
 * ===============================================
 * "Vos réussites de la semaine" côté agent. Comparaison à SOI (jamais aux autres),
 * valorisation des efforts invisibles + reconnaissances reçues du manager.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../../lib/api';
import { Sparkles, TrendingUp, RotateCcw, Award, Share2 } from 'lucide-react';
import ShareStoryModal from './ShareStoryModal';

export default function WeeklySuccessCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showShare, setShowShare] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get('/engagement/reinforcement/weekly-recap');
                if (alive) setData(r.data);
            } catch (e) {
                // silencieux
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    if (loading || !data) return null;

    const delta = data.vs_last_week?.delta ?? 0;
    const recognitions = data.recognitions || [];

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-[#681793]/30 bg-gradient-to-br from-[#681793]/12 to-[#FF914D]/8 p-4 sm:p-5"
            data-testid="weekly-success-card"
        >
            <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-[#FF914D]" />
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                    Vos réussites de la semaine
                </p>
            </div>
            <p className="text-sm sm:text-base font-semibold text-foreground leading-snug" data-testid="weekly-success-headline">
                {data.headline}
            </p>

            <div className="mt-3 grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-black/20 p-2.5">
                    <p className="text-xl font-black text-foreground leading-none" data-testid="weekly-actions-count">
                        {data.total_actions}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">actions de suivi</p>
                </div>
                <div className="rounded-xl bg-black/20 p-2.5">
                    <p className="text-xl font-black text-foreground leading-none flex items-center gap-1">
                        <RotateCcw className="w-4 h-4 text-emerald-400" />
                        {data.reactivations}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">mandats réveillés</p>
                </div>
                <div className="rounded-xl bg-black/20 p-2.5">
                    <p className="text-xl font-black text-foreground leading-none flex items-center gap-1">
                        <Award className="w-4 h-4 text-[#A855F7]" />
                        {data.kudos_count}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">reconnaissances</p>
                </div>
            </div>

            {delta > 0 && (
                <div className="mt-2.5 flex items-center gap-1.5 text-xs text-emerald-400" data-testid="weekly-trend">
                    <TrendingUp className="w-3.5 h-3.5" />
                    {delta} action{delta > 1 ? 's' : ''} de plus que la semaine dernière — vous montez en régime.
                </div>
            )}

            {recognitions.length > 0 && (
                <div className="mt-3 space-y-2">
                    {recognitions.map((r, i) => (
                        <div
                            key={i}
                            className="rounded-xl bg-[#681793]/15 border border-[#681793]/25 p-3"
                            data-testid="weekly-recognition-item"
                        >
                            <div className="flex items-center gap-1.5 text-[11px] text-[#A855F7] font-medium">
                                <Award className="w-3.5 h-3.5" />
                                {r.from_name || 'Votre manager'} vous a reconnu
                            </div>
                            <p className="text-xs text-foreground/85 mt-1 leading-snug">« {r.message} »</p>
                        </div>
                    ))}
                </div>
            )}

            <button
                onClick={() => setShowShare(true)}
                className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border border-[#FF914D]/30 bg-[#FF914D]/10 hover:bg-[#FF914D]/15 text-[#FF914D] text-sm font-medium py-2 transition-colors"
                data-testid="open-share-story"
            >
                <Share2 className="w-4 h-4" />
                Partager une réussite
            </button>

            <ShareStoryModal open={showShare} onOpenChange={setShowShare} />
        </motion.div>
    );
}
